import Card from '../components/card/Card';
import Tooltip from '../components/tooltip/Tooltip';
import Span from '../components/texts/span/Span';
import Badge from '../components/badge/Badge';
import P from '../components/texts/p/P';
import Icon from '../components/icon/Icon';
import TruncatedTextWithTooltip from '../components/TruncatedTextWithTooltip/TruncatedTextWithTooltip';
import { formatCurrency } from '../utils/Masks';
import { format } from 'date-fns';

import { Nullable } from '../types/interfaces';
import {
  getBadgeStatus,
  getStatusText,
} from './applicationStatusUtils';
import { getBalanceReadStatus, getBalanceProcessingErrorMessage } from './balanceReadStatusUtils';
import { getFiscalIdConfig } from './fiscalIdentifier';

export interface IRow {
  cuit: string;
  id: number;
  commercial: string;
  companyName: string;
  producerName: string;
  producerLastName: string;
  updateDate: string;
  status: number;
  /** API application_status.code (FIN/REJ/…) — preferred for UI when present. */
  statusCode?: string;
  totalArs: number;
  totalUsd: number;
  approved_total_ars: Nullable<number>;
  approved_total_usd: Nullable<number>;
  granted_total_ars?: Nullable<number>;
  granted_total_usd?: Nullable<number>;
  usuario?: string;
  referido?: string;
  balanceProcessingStatus?: string | null;
  balanceProcessingErrorCode?: string | null;
  balanceProcessingErrorContext?: string | null;
  pendingApprovalRoleLabel?: string;
  /**
   * Aprobador masivo de comité (Phase 8). Vienen del enrichment del BFF
   * cuando `status === 'COM'` y el flag `isCommitteeBulkApprovalEnabled`
   * está activo. CommitteeDashboard mapea `committee_can_approve` →
   * `canApprove` (camelCase) en el row procesado.
   */
  canApprove?: boolean;
  committeeMode?: 'LEGACY' | 'MEMBER_QUORUM';
  committeeHasNext?: boolean;
  /** Raw is_valid from the backend (Finished folders list, Task 53633): true/false/null. */
  isValid?: boolean | null;
}

export function getDashboardColumns({
  handleRowClicked,
  handleViewApplication,
  showApprovedAmounts = false,
  showMontoOtorgarColumn = false,
  showBalanceReadStatus = false,
  showPendingApproval = false,
  showValidity = false,
  commercialColumnTitle = 'Comercial',
  showBulkSelection = false,
  selectedIds = new Set<number>(),
  onToggleRow = () => {},
  isRowSelectable = () => true,
  isMasterCheckboxChecked = false,
  isMasterCheckboxDisabled = false,
  onTogglePageAll = () => {},
  country,
  localCurrencyCode = 'ARS',
}: {
  handleRowClicked: (row: IRow) => void;
  handleViewApplication?: (row: IRow) => void;
  showApprovedAmounts?: boolean;
  /** Columna "Monto a otorgar" desde listado (flag calculadora máximo) */
  showMontoOtorgarColumn?: boolean;
  showBalanceReadStatus?: boolean;
  showPendingApproval?: boolean;
  /** "Vigente" column (Task 53633): only Finished folders enables it (RB15). */
  showValidity?: boolean;
  /** Con flag gerencia suele mostrarse "Gerencia" en el mismo slot que comercial (patrón A). */
  commercialColumnTitle?: string;
  /**
   * Aprobador masivo de comité (Phase 8). Cuando `true`, agrega una columna
   * de checkbox al inicio del array. Las filas con `isRowSelectable(row) ===
   * false` no renderizan checkbox (D1: oculto, no deshabilitado).
   */
  showBulkSelection?: boolean;
  selectedIds?: Set<number>;
  onToggleRow?: (id: number) => void;
  isRowSelectable?: (row: IRow) => boolean;
  /** Checkbox maestro (Task 52098): selecciona/deselecciona filas visibles de la página. */
  isMasterCheckboxChecked?: boolean;
  isMasterCheckboxDisabled?: boolean;
  onTogglePageAll?: () => void;
  country?: string | null;
  /** Tenant local currency label for amount columns (ARS or UYU). */
  localCurrencyCode?: string;
}) {
  const { label: fiscalIdLabel, format: formatFiscalId } =
    getFiscalIdConfig(country);
  const localCurrencyLabel = localCurrencyCode || 'ARS';
  const formatGrantedLine = (value: Nullable<number> | undefined): string => {
    if (value == null || Number.isNaN(Number(value))) return '—';
    return formatCurrency(value, true);
  };
  const bulkSelectionColumn = showBulkSelection
    ? [
        {
          name: (
            <input
              type='checkbox'
              checked={isMasterCheckboxChecked}
              disabled={isMasterCheckboxDisabled}
              onChange={onTogglePageAll}
              onClick={(e) => e.stopPropagation()}
              data-testid='bulk-master-checkbox'
              style={{ cursor: isMasterCheckboxDisabled ? 'not-allowed' : 'pointer', width: '16px', height: '16px' }}
            />
          ),
          cell: (row: IRow) =>
            isRowSelectable(row) ? (
              <input
                type='checkbox'
                checked={selectedIds.has(row.id)}
                onChange={() => onToggleRow(row.id)}
                onClick={(e) => e.stopPropagation()}
                data-testid={`bulk-row-checkbox-${row.id}`}
                style={{ cursor: 'pointer', width: '16px', height: '16px' }}
              />
            ) : null,
          minWidth: '40px',
          grow: 0.2,
          center: true,
        },
      ]
    : [];
  return [
    ...bulkSelectionColumn,
    {
      name: fiscalIdLabel,
      selector: (row: IRow) => (
        <Card onClick={() => handleRowClicked(row)}>
          <TruncatedTextWithTooltip text={formatFiscalId(row?.cuit || '')}>
            <P type='4' variant='neutral'>
              {formatFiscalId(row?.cuit || '')}
            </P>
          </TruncatedTextWithTooltip>
        </Card>
      ),
      minWidth: '55px',
      grow: 0.5,
    },
    {
      name: 'Usuario',
      selector: (row: IRow) => (
        <Card onClick={() => handleRowClicked(row)}>
          <TruncatedTextWithTooltip text={row?.usuario || 'N/A'}>
            <P type='4' variant='neutral'>
              {row?.usuario || 'N/A'}
            </P>
          </TruncatedTextWithTooltip>
        </Card>
      ),
      minWidth: '55px',
      grow: 0.6,
    },
    {
      name: 'Referido/Vendedor',
      selector: (row: IRow) => (
        <Card onClick={() => handleRowClicked(row)}>
          <TruncatedTextWithTooltip text={row?.referido || 'N/A'}>
            <Span type='4' variant='neutral'>
              {row?.referido || 'N/A'}
            </Span>
          </TruncatedTextWithTooltip>
        </Card>
      ),
      minWidth: '55px',
      grow: 0.6,
    },
    {
      name: commercialColumnTitle,
      selector: (row: IRow) => (
        <Card onClick={() => handleRowClicked(row)}>
          <TruncatedTextWithTooltip text={row?.commercial || 'Sin asignar'}>
            <P type='4' variant='neutral'>
              {row?.commercial || 'Sin asignar'}
            </P>
          </TruncatedTextWithTooltip>
        </Card>
      ),
      minWidth: '45px',
      grow: 0.6,
    },
    {
      name: 'Razón social',
      selector: (row: IRow) => (
        <Card onClick={() => handleRowClicked(row)}>
          <TruncatedTextWithTooltip text={row?.companyName || ''}>
            <Span type='4' variant='neutral'>
              {row?.companyName || ''}
            </Span>
          </TruncatedTextWithTooltip>
        </Card>
      ),
      minWidth: '50px',
      grow: 0.7,
    },
    {
      name: 'Modificado',
      cell: (row: IRow) => {
        const date = row?.updateDate ? new Date(row?.updateDate) : null;
        return (
          <Card onClick={() => handleRowClicked(row)}>
            <P type='4' variant='neutral'>
              {date ? format(date, 'dd/MM/yyyy') : ''}
            </P>
          </Card>
        );
      },
      minWidth: '50px',
      grow: 0.4,
    },
    ...(showValidity
      ? [
          {
            name: 'Vigente',
            id: 'validity',
            cell: (row: IRow) => {
              const value = row?.isValid;
              const text = value === true ? 'SI' : value === false ? 'NO' : '—';
              const color = value === true ? '#1E9E4A' : value === false ? '#D14343' : '#8A8A8A';
              return (
                <P type='4' variant='neutral' style={{ color, fontWeight: 600 }} withMargin={false}>
                  {text}
                </P>
              );
            },
            center: true,
            minWidth: '90px',
            grow: 0.5,
            sortable: false,
          },
        ]
      : []),
    {
      name: showMontoOtorgarColumn
        ? 'Monto a otorgar'
        : showApprovedAmounts
          ? 'Monto aprobado'
          : 'Monto',
      cell: (row: IRow) => (
        <Card onClick={() => handleRowClicked(row)}>
          {showMontoOtorgarColumn ? (
            <>
              <Card display='flex' gap='2px' margin={{ bottom: '1' }}>
                <P type='2' withMargin={false} variant='neutral' weight='bold'>
                  {localCurrencyLabel}
                </P>
                <P type='2' withMargin={false} variant='neutral'>
                  {formatGrantedLine(row.granted_total_ars)}
                </P>
              </Card>
              <Card display='flex' gap='2px'>
                <P type='2' withMargin={false} variant='neutral' weight='bold'>
                  USD
                </P>
                <P type='2' withMargin={false} variant='neutral'>
                  {formatGrantedLine(row.granted_total_usd)}
                </P>
              </Card>
            </>
          ) : showApprovedAmounts ? (
            <>
              <Card display='flex' gap='2px' margin={{ bottom: '1' }}>
                <P type='2' withMargin={false} variant='neutral' weight='bold'>
                  {localCurrencyLabel}
                </P>
                <P type='2' withMargin={false} variant='neutral'>
                  {formatCurrency(row.approved_total_ars ?? 0, true)}
                </P>
              </Card>
              <Card display='flex' gap='2px'>
                <P type='2' withMargin={false} variant='neutral' weight='bold'>
                  USD
                </P>
                <P type='2' withMargin={false} variant='neutral'>
                  {formatCurrency(row.approved_total_usd ?? 0, true)}
                </P>
              </Card>
            </>
          ) : (
            <>
              <Card display='flex' gap='2px' margin={{ bottom: '1' }}>
                <P type='2' withMargin={false} variant='neutral' weight='bold'>
                  {localCurrencyLabel}
                </P>
                <P type='2' withMargin={false} variant='neutral'>
                  {formatCurrency(row.totalArs ?? 0, true)}
                </P>
              </Card>
              <Card display='flex' gap='2px'>
                <P type='2' withMargin={false} variant='neutral' weight='bold'>
                  USD
                </P>
                <P type='2' withMargin={false} variant='neutral'>
                  {formatCurrency(row.totalUsd ?? 0, true)}
                </P>
              </Card>
            </>
          )}
        </Card>
      ),
      minWidth: '140px',
      grow: 0.6,
    },
    ...(showBalanceReadStatus
      ? [
          {
            name: 'Estado lectura',
            cell: (row: IRow) => {
              const readStatus = getBalanceReadStatus(row.balanceProcessingStatus);
              const showFailureTooltip =
                row.balanceProcessingStatus === 'FAILED' &&
                row.balanceProcessingErrorCode;
              const failureMessage = getBalanceProcessingErrorMessage(
                row.balanceProcessingErrorCode,
                row.balanceProcessingErrorContext,
                country
              );
              const cellContent = (
                <Card onClick={() => handleRowClicked(row)}>
                  <Card display='flex' alignItems='center' gap='8px'>
                    {readStatus.icon && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '16px', height: '16px', flexShrink: 0, overflow: 'hidden' }}>
                        <span style={{ display: 'inline-block', width: '16px', height: '16px', lineHeight: 0 }}>
                          <Icon
                            name={readStatus.icon}
                            variant={readStatus.variant}
                            width={16}
                            height={16}
                          />
                        </span>
                      </span>
                    )}
                    <P type='4' variant='neutral'>
                      {readStatus.text}
                    </P>
                  </Card>
                </Card>
              );
              return showFailureTooltip ? (
                <Tooltip msg={failureMessage}>
                  {cellContent}
                </Tooltip>
              ) : (
                cellContent
              );
            },
            minWidth: '45px',
            grow: 0.5,
          },
          {
            name: 'Solicitud',
            cell: (row: IRow) => (
              <div
                style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => {
                  if (handleViewApplication) {
                    handleViewApplication(row);
                  }
                }}
                role='button'
                aria-label='Ver solicitud'
              >
                <Icon name='view_outline' variant='info' modifier='strong' />
              </div>
            ),
            center: true,
            minWidth: '45px',
            grow: 0.5,
          },
        ]
      : []),
    ...(showPendingApproval
      ? [
          {
            name: 'Pendiente de aprobación',
            selector: (row: IRow) => (
              <Card onClick={() => handleRowClicked(row)}>
                <TruncatedTextWithTooltip text={row?.pendingApprovalRoleLabel ?? '—'}>
                  <P type='4' variant='neutral'>
                    {row?.pendingApprovalRoleLabel ?? '—'}
                  </P>
                </TruncatedTextWithTooltip>
              </Card>
            ),
            minWidth: '120px',
            grow: 0.6,
          },
        ]
      : []),
    {
      name: 'Estado',
      id: 'status',
      cell: (row: IRow) => {
        const statusKey = row?.statusCode ?? row?.status;
        const badgeVariant = getBadgeStatus(statusKey);
        const statusText = getStatusText(statusKey);

        return (
          <Card alignItems='center' onClick={() => handleRowClicked(row)}>
            <Badge variant={badgeVariant} type='text'>
              {statusText}
            </Badge>
          </Card>
        );
      },
      minWidth: '110px',
      grow: 0.7,
    },
  ];
}
